import { type RowData, functionalUpdate } from '@tanstack/react-table';

import type { DataTableColumnFontStyleProps } from './column-font-style-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import type {
  Column,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';

/**
 * Feature implementation for FontStyle. This contains the logic that will be given
 * to the tanstack table. The implementing interface is important as these are
 * the function that will be called by the tanstack table core during render.
 * @internal
 */
export const DataTableColumnFontStyle: TableFeature = {
  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    // Add a toggleFontStyle function onto the column so that it can be accessed
    // during rendering.
    column.toggleFontStyle = (value) => {
      table.setColumnFontStyle((old) => {
        let newValue = value;
        // If there is no passed value, apply the toggle logic
        if (newValue === undefined) {
          if (old[column.id] !== undefined) {
            newValue = old[column.id] === 'code' ? 'text' : 'code';
          } else {
            newValue =
              table.getVisualVariantOptions().fontStyle === 'code'
                ? 'text'
                : 'code';
          }
        }
        return {
          ...old,
          [column.id]: newValue,
        };
      });
    };

    // Helper function that will allow access to the columnFontStyle state of an
    // individual column.
    column.getFontStyle = () => {
      const fontStyleState = table.getState().columnFontStyle;

      // Check if a font style is defined in the state
      if (fontStyleState?.[column.id]) {
        return fontStyleState?.[column.id];
      }
      // Check if some font style is defined in the columnDef meta based on column types
      return (
        column.columnDef.meta?.fontStyle ??
        table.getVisualVariantOptions().fontStyle
      );
    };
  },

  createTable<TData extends RowData>(table: Table<TData>): void {
    table.setColumnFontStyle = (updater) => {
      const newValue = functionalUpdate(
        updater,
        table.getState().columnFontStyle ?? {},
      );
      table.options.onColumnFontStyleChange?.(newValue);
    };
  },
};

/**
 * Configuration hook for the DataTable FontStyle feature.
 * @internal
 */
export function useColumnFontStyle<T>(
  props: DataTableColumnFontStyleProps,
  options: TableOptions<T>,
) {
  const configFontStyle = useDataTableConfigElement('fontStyle');

  const [columnFontStyle, onColumnFontStyleChange] = useControllableState({
    value: props.fontStyle ?? configFontStyle,
    defaultValue: props.defaultFontStyle,
    onChange: props.onFontStyleChange,
  });

  options.state = { ...options.state, columnFontStyle };
  options.onColumnFontStyleChange = onColumnFontStyleChange;
}
